import { action, computed, observable, runInAction } from 'mobx';
import { ProjectsRootVisualStore } from '../../common/stores';
import type { PromptDependentPackage } from '../types';
import { AdministrationService } from '../../project_management/services/AdministrationService';
import PromptTemplateService from '../services/PromptTemplatesService';
import EditPromptTemplateStore from './EditPromptTemplatesStore';
import { showDocumentProcessingResults } from '../../common/components/DocumentProcessingResults';
import { documentProcessingFailureOutcomes } from '../../common/misc/documentProcessingOutcomes';

export default class PropmtDependentPackagesStore {
    @observable
    isLoadingDependentPackages: boolean = false;

    @observable
    dependentPackages: PromptDependentPackage[] = [];

    constructor(
        private readonly projectsStore: ProjectsRootVisualStore,
        private readonly promptTemplateEditStore: EditPromptTemplateStore,
        private readonly service: PromptTemplateService,
        private readonly administrationService: AdministrationService
    ) {}

    @computed
    get project() {
        return this.projectsStore.currentProject;
    }

    @computed
    get selectedTemplate() {
        return this.promptTemplateEditStore.selectedTemplate;
    }

    @computed
    get dependentPackageCount() {
        return this.dependentPackages.length;
    }

    @computed
    get isLoading() {
        return this.promptTemplateEditStore.isLoadingTemplate || this.isLoadingDependentPackages;
    }

    @computed
    get reprocessDisabled() {
        return this.isLoading || !this.dependentPackageCount;
    }

    @action.bound
    async getDependentPackages() {
        if (!this.project || !this.selectedTemplate) {
            return;
        }

        runInAction(() => {
            this.isLoadingDependentPackages = true;
        });

        const dependentPackages = await this.service.getDependentPackages(this.project.id, this.selectedTemplate.id);

        runInAction(() => {
            this.dependentPackages = dependentPackages;
            this.isLoadingDependentPackages = false;
        });
    }

    async reprocessDependentPackages(documents: PromptDependentPackage[], projectId: string, promptTemplateId: string) {
        const dependentPackageIds = documents.map(document => document.id);
        if (!dependentPackageIds.length) {
            return;
        }
        const outcomeDocuments = documents.map(document => ({ packageId: document.id, fileName: document.fileName }));
        // A lost response may follow admission. Do not retry or imply that nothing started.
        const result = await this.administrationService
            .runInterpretationBatch(dependentPackageIds, projectId, promptTemplateId)
            .catch(() => undefined);
        if (result?.isOk()) {
            showDocumentProcessingResults(result.value);
            return;
        }
        showDocumentProcessingResults(
            documentProcessingFailureOutcomes(outcomeDocuments, result?.isErr() ? result.error : undefined)
        );
    }

    dispose() {
        runInAction(() => {
            this.dependentPackages = [];
            this.isLoadingDependentPackages = false;
        });
    }
}
